import { DOCUMENT } from '@angular/common';
import {
	Component,
	Inject,
	Injectable,
	OnDestroy,
	OnInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeveloperService } from '@app/services/developer/developer.service';
import { cast, IQuestionnaireGroup, Messgeraete } from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { inRange, isEqual } from 'lodash';
import { Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { WizardPage } from '@workflow/components/navigator/wizard-view/interfaces';
import { AbstractSpecialQuestionComponent, ISpecialQuestion, ISpecialQuestionComponent } from '../special-question.service';
import { isNil } from 'lodash';
import { LabyrinthTestEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/labyrinth-test/labyrinth-test-epilog.component';

@Injectable()
export class LabyrinthTest implements ISpecialQuestion {
	public readonly component = LabyrinthTestComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'LabyrinthTest';
	public readonly requiredDevices = [Messgeraete.TouchScreen, Messgeraete.SoundDevice];

	title = 'Labyrinth Test';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: 'Labyrinth Test',
		//FIXME GSP-379
		preface: [
			`Beim Labyrinth-Test ist es Ihre Aufgabe, <b>so schnell wie möglich</b> den unter den blauen Punkten versteckten Weg zu finden,
			der von dem linken unteren orangen Punkt nach oben rechts zum anderen orangenen Punkt verläuft.
			Für den richtigen Weg stehen hohe Töne, tiefe Töne zeigen Ihnen den falschen Weg an.
			Der Labyrinthweg kann seine Richtung beliebig, aber stets nur im rechten Winkel und <b>niemals diagonal</b> ändern. Es gibt keine Sackgassen.
			Wichtig ist es, jeden Punkt einzeln zu berühren bzw. anzutippen - <b>bitte nicht “ziehen”.</b><br><br>
			<div class="markdown-html">
				<div>Beispiel: Hoher Ton <audio controls="controls" class="vert-align-middle"><source type="audio/mp3"
					src='assets/sounds/Hoher Ton.mp3' /></audio></div>
				<br><br>
				<div>Beispiel: Tiefer Ton <audio controls="controls" class="vert-align-middle"><source type="audio/mp3"
					src='assets/sounds/Tiefer Ton.mp3' /></audio></div>
				<br>
			</div>`
		],
		items: [
			cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Labyrinth Test - erstes Labyrinth',
				items: [
					{
						key: '0.zeit',
						label: 'Zeit',
						question: cast<RangeQuestion>({
							type: 'range',
							unit: 'sek',
							rangeLow: 0,
							rangeHigh: 250,
						})
					},
					{
						key: '0.wiederholungsfehler',
						label: 'Wiederholungsfehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 100
						})
					},
					{
						key: '0.probefehler',
						label: 'Probefehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 100
						})
					}
				],
				isProcedure: true,
			}), cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Labyrinth Test - zweites Labyrinth',
				items: [
					{
						key: '1.zeit',
						label: 'Zeit',
						question: cast<RangeQuestion>({
							type: 'range',
							unit: 'sek',
							rangeLow: 0,
							rangeHigh: 250
						})
					},
					{
						key: '1.wiederholungsfehler',
						label: 'Wiederholungsfehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 100
						})
					},
					{
						key: '1.probefehler',
						label: 'Probefehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 100
						})
					}
				],
				isProcedure: true,
			}), cast<BlockQuestionItem>({
				type: 'BlockQuestion',
				title: 'Labyrinth Test - drittes Labyrinth',
				items: [
					{
						key: '2.zeit',
						label: 'Zeit',
						question: cast<RangeQuestion>({
							type: 'range',
							unit: 'sek',
							rangeLow: 0,
							rangeHigh: 250
						})
					},
					{
						key: '2.wiederholungsfehler',
						label: 'Wiederholungsfehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 100
						})
					},
					{
						key: '2.probefehler',
						label: 'Probefehler',
						question: cast<RangeQuestion>({
							type: 'range',
							rangeLow: 0,
							rangeHigh: 100
						})
					}
				],
				isProcedure: true,
			}),
		],
	};

	public isCompleted(currentValue: ResultTypeLabyrinth): boolean {
		return currentValue?.fehlwert || currentValue && Object.keys(currentValue).length === 3
			&& (Object.values(currentValue) as ResultTypeLabyrinthElement[]).every(value => !isNil(value.probefehler) && !isNil(value.wiederholungsfehler) && !isNil(value.zeit));
	}

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {...specialQuestion, title: group.title + ' – Feld I'},
			subpage: 0,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
		},
		{
			...group,
			question: {...specialQuestion, title: group.title + ' – Feld II'},
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
		},
		{
			...group,
			question: {...specialQuestion, title: group.title + ' – Feld III'},
			subpage: 2,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: LabyrinthTestEpilogComponent,
			key: specialQuestion.key,
		}];
	}

}

export interface ResultTypeLabyrinthElement {
	zeit: number;
	wiederholungsfehler: number;
	probefehler: number;
	cancelled?: boolean;
}

export interface ResultTypeLabyrinth {
	[key: number]: ResultTypeLabyrinthElement,
	fehlwert?: boolean,
}

enum Tiles {
	emptyTile,
	validTile,
	startTile,
	exitTile,
}
type MazeCoords = {x?: number, y?: number};
type MazeTile = {
	coordinates: MazeCoords,
	myType: Tiles,
};
type PathTile = {
	previous: PathTile,
	next: PathTile,
	coordinates: MazeCoords,
	myType: Tiles,
	visited: boolean,
};

@Component({
	selector: 'workflow-labyrinth-test',
	templateUrl: './labyrinth-test.component.html',
	styleUrls: ['./labyrinth-test.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class LabyrinthTestComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, ISpecialQuestionComponent {

	readonly stdTile = Tiles;

	currentValidPath: PathTile[][] = [[]];
	labProp = {
		height: 0,
		width: 0,
	};
	finished: boolean;
	isDevMode = false;
	results: ResultTypeLabyrinth = {};
	started: boolean;

	private subscription = new Subscription();

	private currentLabyrinth: Tiles[][] = [[]];

	private pageId: number;
	private startTime: number;

	//FIXME GSP-378;
	private currentAudio: HTMLAudioElement|null = null;
	private audioHigh = new Audio('assets/sounds/Hoher Ton.mp3');
	private audioLow = new Audio('assets/sounds/Tiefer Ton.mp3');
	private dpcm = 36.13692864308267809768476178486; //Pixels per centimeter @1080p/24", under the assumption that each pixel is perfectly square.

	public labyrinthe = [
		[
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.exitTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.startTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
		],
		[
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.exitTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile],
			[Tiles.startTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
		],
		[
			[Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.exitTile],
			[Tiles.validTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.validTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.emptyTile,Tiles.emptyTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
			[Tiles.startTile,Tiles.validTile,Tiles.validTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile,Tiles.emptyTile],
		]
	];

	constructor(
		private workflowItemKey: WorkflowItemKeyDirective,
		@Inject(DOCUMENT) private document: Document,
		private devService: DeveloperService,
		private route: ActivatedRoute,
		router: Router,
	) {
		super(router);
	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.results = data as ResultTypeLabyrinth;
				this.goToEpilog();
			}
		}));

		this.subscription.add(this.workflowItemKey.property$.subscribe(data => {
			if (!this.finishedAndLocked && !Object.keys(this.results).length) {
				this.results = data as ResultTypeLabyrinth || {};
			}
		}));

		this.subscription.add(this.paramPageId$.subscribe());
		this.subscription.add(this.devService.isDemoMode$.subscribe(mode => {
			this.isDevMode = mode;
		}));

		this.initPage();
		this.audioHigh.load();
		this.audioLow.load();
	}
	private get paramPageId$(): Observable<string> {
		return this.route.paramMap.pipe(
			switchMap(params => of(params.get('pageId'))),
			distinctUntilChanged(),
			tap(pageId => this.pageId = parseInt(pageId)),
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.audioLow.pause();
		this.audioHigh.pause();
	}

	private initPage(): void {
		this.started = false;
		this.finished = false;
		this.valid.emit(this.finished);

		this.initLabyrinth();
		this.initPath();
	}
	private initLabyrinth(): void {
		this.labProp = {
			height: this.dpcm * 14,
			width: this.dpcm * 14,
		};

		this.currentLabyrinth = this.labyrinthe[this.page];

		this.results[this.page] = {
			zeit: null,
			wiederholungsfehler: 0,
			probefehler: 0,
		};
	}
	private initPath(): void {
		this.currentValidPath = [[],[],[],[],[],[],[],[],[],[]];

		// Start-Tile
		let recentTile: PathTile = {previous: null, next: null, visited: false, coordinates: {x: 0,y: 9}, myType: Tiles.startTile};
		this.currentValidPath[9][0] = recentTile;

		let exitFound = false;
		while (!exitFound) {
			const recentCoords = recentTile.coordinates;
			// Wir iterieren gegen den Uhrzeigersinn, links beginnend um das aktuelle Tile
			const nextOffset = [[-1,0],[0,-1],[1,0],[0,1]].find(([x,y]) => {
				const newCoords: MazeCoords = {x: recentCoords.x + x, y: recentCoords.y + y};
				if (!inRange(newCoords.x, 0, 10) || !inRange(newCoords.y, 0, 10)) {
					// keine valide Koordinate
					return false;
				}
				const nextTile: Tiles = this.currentLabyrinth[newCoords.y][newCoords.x];
				if (nextTile !== Tiles.validTile && nextTile !== Tiles.exitTile) {
					// kein relevantes Pfad-Tile
					return false;
				}
				if (isEqual(recentTile.previous?.coordinates, newCoords)) {
					// das Vorgänger-Pfad-Tile ist irrelevant
					return false;
				}
				if (nextTile === Tiles.exitTile) {
					exitFound = true;
					return true;
				}
				return true;
			});
			if (!nextOffset) {
				throw new Error('Uncomplete maze');
			}
			// Kette bauen
			const nextTile = {
				previous: recentTile,
				next: null,
				visited: false,
				coordinates: {x: recentCoords.x + nextOffset[0],y: recentCoords.y + nextOffset[1]},
				myType: Tiles.validTile,
			};
			recentTile.next = nextTile;
			recentTile = nextTile;
			// Tile in Map speichern
			this.currentValidPath[nextTile.coordinates.y][nextTile.coordinates.x] = recentTile;
		}
		recentTile.myType = Tiles.exitTile;
	}

	private startTestIfNecessary(tile: Tiles): void {
		if (this.started || tile !== Tiles.startTile) {
			return;
		}

		this.started = true;
		this.startTime = performance.now();
	}

	private processTileOperation(currentTile: MazeTile): void {
		if (!this.started || this.finished) {
			return;
		}
		if (this.doProbeFehler(currentTile)) {
			return;
		}
		if (this.doWiederholungsFehler(currentTile)) {
			return;
		}
		if (this.doMarkValidPath(currentTile)) {
			this.doFinalMoveIfNecessary();
			return;
		}
	}

	private doProbeFehler(currentTile: MazeTile): boolean {
		if (currentTile.myType !== Tiles.emptyTile) {
			return false;
		}
		this.results[this.page].probefehler++;
		this.playSound('tief');

		return true;
	}
	private doWiederholungsFehler(currentTile: MazeTile): boolean {
		if (currentTile.myType === Tiles.emptyTile) {
			return false;
		}
		if (!this.isAlreadyVisitedPathSegment(currentTile)){
			return false;
		}
		this.results[this.page].wiederholungsfehler++;
		this.playSound('hoch');

		return true;
	}
	private doMarkValidPath(currentTile: MazeTile): boolean {
		if (currentTile.myType === Tiles.emptyTile) {
			return false;
		}

		this.playSound('hoch');
		this.currentValidPath[currentTile.coordinates.y][currentTile.coordinates.x].visited = true;

		return true;
	}
	private doFinalMoveIfNecessary(): boolean {
		if (!this.isPathCompleted()) {
			return false;
		}

		this.finished = true;
		this.results[this.page].zeit = (performance.now() - this.startTime)/1000;
		this.valid.emit(true);

		this.workflowItemKey.setProperty(this.results);

		return true;
	}

	private isAlreadyVisitedPathSegment(currentTile: MazeTile): boolean {
		return !!((this.currentValidPath[currentTile.coordinates.y] || [])[currentTile.coordinates.x] || {}).visited;
	}
	private isPathCompleted(): boolean {
		let tile: PathTile = this.currentValidPath[9][0];
		let isCompleted = tile.visited;
		while (tile?.next && isCompleted) {
			tile = tile.next;
			if (!tile.visited) {
				isCompleted = false;
				break;
			}
		}

		return isCompleted;
	}

	private playSound(type: 'tief'|'hoch'): void {
		if (this.currentAudio) {
			this.currentAudio.pause();
			this.currentAudio.currentTime = 0;
		}
		this.currentAudio = type === 'tief' ? this.audioLow : this.audioHigh;
		this.currentAudio.play().catch(console.error);
	}

	private getTilesByCoordinates(coords: MazeCoords): Tiles|null {
		const tile = (this.currentLabyrinth[coords.y] || [])[coords.x];

		return (tile >= 0) ? tile : null;
	}

	processTappedObject(coords: MazeCoords): void {
		const tile = this.getTilesByCoordinates(coords);
		if (tile === null) {
			return null;
		}
		this.startTestIfNecessary(tile);

		this.processTileOperation({myType: tile, coordinates: coords});
	}

	loadNextLabyrinth(): void {
		if (this.page >= 2) {
			this.goToEpilog();
		} else {
			this.goToNext();
		}
	}
	cancelLabyrinth(): void {
		this.results[this.page] = {
			zeit: null,
			probefehler: 0,
			wiederholungsfehler: 0,
			cancelled: true,
		};
		this.finished = true;
		this.valid.emit(true);

		this.workflowItemKey.setProperty(this.results);

		this.loadNextLabyrinth();
	}

}
